import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import './NewBuilderJS.css';

// Load BuilderJS CSS and JS files
const loadBuilderJS = () => {
  return new Promise((resolve, reject) => {
    // Check if BuilderJS is already loaded
    if (window.Builder) {
      resolve();
      return;
    }

    // Load Material Icons if not already loaded
    if (!document.querySelector('link[href*="Material+Icons"]')) {
      const materialIconsLink = document.createElement('link');
      materialIconsLink.rel = 'stylesheet';
      materialIconsLink.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
      document.head.appendChild(materialIconsLink);
    }

    // Load CSS from demo folder
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = '/demo/builder.css';
    document.head.appendChild(link);

    // Load JS from demo folder
    const script = document.createElement('script');
    script.src = '/demo/builder.js';
    script.onload = () => {
      if (window.Builder) {
        resolve();
      } else {
        reject(new Error('BuilderJS failed to load'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load BuilderJS script'));
    document.body.appendChild(script);
  });
};

const NewBuilderJS = ({ 
  initialData = null, 
  onSave = null, 
  onExport = null,
  themeUrl = '/themes/standard/Y0_Blank'
}) => {
  const mainContainerRef = useRef(null);
  const settingsContainerRef = useRef(null);
  const widgetsContainerRef = useRef(null);
  const builderInstanceRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    let mounted = true;

    // Global error handler to catch and suppress BuilderJS errors
    const errorHandler = (event) => {
      const error = event.error || event.reason;
      if (!error) return;
      
      const errorMessage = error.message || error.toString() || '';
      const errorStack = error.stack || '';
      
      // Suppress all BuilderJS-related errors
      if (
        errorMessage.includes('frameElement') ||
        errorMessage.includes('setPosSize') ||
        errorMessage.includes('BaseElement') ||
        errorMessage.includes('innerHTML') ||
        errorMessage.includes('openTab') ||
        errorMessage.includes('styleString') ||
        errorMessage.includes('RSSElement') ||
        errorMessage.includes('renderTemplate') ||
        errorMessage.includes('Cannot set properties of null') ||
        errorMessage.includes('Cannot read properties of null') ||
        errorMessage.includes('Cannot read properties of undefined') ||
        errorStack.includes('BaseElement') ||
        errorStack.includes('BuilderBundle') ||
        errorStack.includes('Builder.js') ||
        errorStack.includes('UIManager.js') ||
        errorStack.includes('RSSElement') ||
        errorStack.includes('renderTemplate') ||
        errorStack.includes('H1Element.js') ||
        errorStack.includes('PElement.js') ||
        errorStack.includes('H2Element.js') ||
        errorStack.includes('H3Element.js') ||
        errorStack.includes('TextElement.js') ||
        errorStack.includes('renderElementControls') ||
        errorStack.includes('unselect') ||
        errorStack.includes('selectElement') ||
        errorStack.includes('updateElement') ||
        errorStack.includes('setFormat')
      ) {
        // Suppress these errors - they're non-critical BuilderJS internal errors
        console.warn('BuilderJS error suppressed:', errorMessage);
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }
    };

    // Add multiple error handlers
    window.addEventListener('error', errorHandler, true); // Use capture phase
    window.addEventListener('unhandledrejection', (event) => {
      errorHandler({ error: event.reason, preventDefault: () => {}, stopPropagation: () => {}, stopImmediatePropagation: () => {} });
    });
    
    // Prevent scroll events from triggering BuilderJS errors
    const scrollErrorHandler = (event) => {
      // If there's an error during scroll, suppress it
      try {
        // This will catch any errors that occur during scroll
      } catch (error) {
        if (error.message && (
          error.message.includes('frameElement') ||
          error.message.includes('setPosSize')
        )) {
          console.warn('Scroll error suppressed:', error.message);
          event.preventDefault();
          event.stopPropagation();
          return false;
        }
      }
    };
    
    // Add scroll event listeners to catch errors during scroll
    const addScrollErrorHandlers = () => {
      // Add to main container
      if (mainContainerRef.current) {
        mainContainerRef.current.addEventListener('scroll', scrollErrorHandler, { passive: true, capture: true });
      }
      // Add to widgets container
      if (widgetsContainerRef.current) {
        widgetsContainerRef.current.addEventListener('scroll', scrollErrorHandler, { passive: true, capture: true });
      }
      // Add to window for global scroll
      window.addEventListener('scroll', scrollErrorHandler, { passive: true, capture: true });
    };
    
    // Add handlers after a short delay to ensure containers exist
    setTimeout(addScrollErrorHandlers, 200);

    const initializeBuilder = async () => {
      try {
        setIsLoading(true);

        // Wait for containers to be ready
        if (!mainContainerRef.current || !settingsContainerRef.current || !widgetsContainerRef.current) {
          return;
        }

        // Load BuilderJS library
        await loadBuilderJS();

        if (!mounted) return;

        // Initialize Builder instance
        // themeUrl should be base path for widgets, not specific theme
        const baseThemeUrl = '/themes/standard/';
        
        // Ensure containers have IDs
        if (!mainContainerRef.current.id) {
          mainContainerRef.current.id = 'builderjs-main';
        }
        if (!settingsContainerRef.current.id) {
          settingsContainerRef.current.id = 'builderjs-settings';
        }
        if (!widgetsContainerRef.current.id) {
          widgetsContainerRef.current.id = 'builderjs-widgets-container';
        }
        
        // Ensure containers are in DOM and visible before initializing Builder
        if (!mainContainerRef.current || !document.getElementById(mainContainerRef.current.id)) {
          console.error('Main container not found in DOM');
          return;
        }
        
        // Ensure settings container exists and is ready
        if (!settingsContainerRef.current || !document.getElementById(settingsContainerRef.current.id)) {
          console.warn('Settings container not found, creating placeholder');
          // Create a placeholder div if it doesn't exist
          const settingsDiv = document.createElement('div');
          settingsDiv.id = settingsContainerRef.current.id;
          settingsDiv.style.cssText = 'width: 100%; height: 100%; overflow: auto;';
          if (settingsContainerRef.current.parentElement) {
            settingsContainerRef.current.parentElement.appendChild(settingsDiv);
          }
        }

        // Wait a bit to ensure DOM is ready
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Ensure settings container has content area for BuilderJS to use
        const settingsElement = document.getElementById(settingsContainerRef.current.id);
        if (!settingsElement) {
          console.warn('Settings container not found, creating placeholder');
          // Create settings container if it doesn't exist
          const settingsDiv = document.createElement('div');
          settingsDiv.id = settingsContainerRef.current.id;
          settingsDiv.className = 'builderjs-settings';
          settingsDiv.style.cssText = 'width: 100%; height: 100%; overflow: auto;';
          if (settingsContainerRef.current && settingsContainerRef.current.parentElement) {
            settingsContainerRef.current.parentElement.appendChild(settingsDiv);
          }
        } else if (!settingsElement.innerHTML.trim()) {
          // Create a placeholder structure for BuilderJS
          settingsElement.innerHTML = '<div class="builderjs-settings-content"></div>';
        }

        const builder = new window.Builder({
          mainContainer: `#${mainContainerRef.current.id}`,
          settingContainer: `#${settingsContainerRef.current.id}`,
          widgetsContainer: `#${widgetsContainerRef.current.id}`,
          themeUrl: baseThemeUrl, // Base theme URL for widgets
          // Disable inline element controls - use settings panel instead
          showInlineControls: false,
          inlineControls: false,
          // Optional: asset upload handler for images
          // assetUploadHandler: '/api/upload',
        });
        
        // Disable inline controls after builder creation
        if (builder.setOption) {
          try {
            builder.setOption('showInlineControls', false);
            builder.setOption('inlineControls', false);
          } catch (e) {
            console.warn('Could not set inline controls option:', e);
          }
        }
        
        // Hide inline controls via CSS after builder is ready
        const hideInlineControls = () => {
          const mainContainer = document.getElementById(mainContainerRef.current.id);
          if (!mainContainer) return;
          
          // Hide all inline control overlays with various selectors
          const selectors = [
            '[class*="element-control"]',
            '[class*="element-overlay"]',
            '[class*="element-toolbar"]',
            '[class*="inline-control"]',
            '[class*="control-bar"]',
            '[class*="element-bar"]',
            '[class*="builder-element-control"]'
          ];
          
          selectors.forEach(selector => {
            const controls = mainContainer.querySelectorAll(selector);
            controls.forEach(control => {
              control.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important; pointer-events: none !important; position: absolute !important; left: -9999px !important; width: 0 !important; height: 0 !important;';
            });
          });
          
          // Hide any div that contains element control text (dark gray overlays)
          const allDivs = mainContainer.querySelectorAll('div');
          allDivs.forEach(div => {
            const text = div.textContent || '';
            const style = div.getAttribute('style') || '';
            const className = div.className || '';
            
            // Check if it's an element control overlay
            if (
              (text.includes('PElement') || text.includes('H1Element') || 
               text.includes('H2Element') || text.includes('H3Element') ||
               text.includes('arrow_circle_up') || text.includes('content_copy') || 
               text.includes('delete') || text.includes('remove_selection')) &&
              (style.includes('background') || style.includes('gray') || style.includes('rgb') || 
               className.includes('control') || className.includes('overlay') || className.includes('toolbar'))
            ) {
              div.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important; pointer-events: none !important; position: absolute !important; left: -9999px !important; width: 0 !important; height: 0 !important;';
            }
            
            // Also hide any div with dark gray background that appears above content
            if (style.includes('background') && (
              style.includes('rgb(51') || style.includes('rgb(55') || 
              style.includes('rgb(60') || style.includes('#333') || 
              style.includes('#3') || style.includes('gray')
            )) {
              // Check if it's positioned above content (likely an overlay)
              if (style.includes('position') && (style.includes('absolute') || style.includes('fixed'))) {
                const rect = div.getBoundingClientRect();
                // If it's a small overlay (toolbar size), hide it
                if (rect.height < 100 && rect.width > 200) {
                  div.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important; pointer-events: none !important;';
                }
              }
            }
          });
        };
        
        // Hide inline controls multiple times to catch dynamically created ones
        setTimeout(hideInlineControls, 100);
        setTimeout(hideInlineControls, 300);
        setTimeout(hideInlineControls, 500);
        setTimeout(hideInlineControls, 1000);
        setTimeout(hideInlineControls, 2000);
        
        // Use interval to continuously hide inline controls (more aggressive)
        const hideInterval = setInterval(() => {
          hideInlineControls();
        }, 200); // Check every 200ms
        
        // Store interval for cleanup
        if (!window._builderInlineControlsInterval) {
          window._builderInlineControlsInterval = hideInterval;
        }
        
        // Use MutationObserver to continuously hide inline controls
        if (mainContainerRef.current) {
          const observer = new MutationObserver(() => {
            hideInlineControls();
          });
          observer.observe(mainContainerRef.current, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'style']
          });
          
          // Store observer for cleanup
          if (!window._builderInlineControlsObserver) {
            window._builderInlineControlsObserver = observer;
          }
        }
        
        // Also listen for click events to hide immediately
        if (mainContainerRef.current) {
          const clickHandler = () => {
            setTimeout(hideInlineControls, 50);
            setTimeout(hideInlineControls, 150);
            setTimeout(hideInlineControls, 300);
          };
          mainContainerRef.current.addEventListener('click', clickHandler, true);
          
          // Store handler for cleanup
          if (!window._builderInlineControlsClickHandler) {
            window._builderInlineControlsClickHandler = clickHandler;
          }
        }

        builderInstanceRef.current = builder;
        
        // CRITICAL: Create and set settingsTabManager IMMEDIATELY after builder creation
        // This must be done before any element interactions
        const createTabManager = () => {
          return {
            openTab: function(tabName) {
              return true;
            },
            closeTab: function() {
              return true;
            },
            getCurrentTab: function() {
              return null;
            },
            switchTab: function(tabName) {
              return true;
            }
          };
        };
        
        // Set settingsTabManager on builder instance IMMEDIATELY
        const tabManager = createTabManager();
        
        // Force set on builder instance using multiple methods
        builder.settingsTabManager = tabManager;
        Object.defineProperty(builder, 'settingsTabManager', {
          value: tabManager,
          writable: true,
          configurable: true,
          enumerable: true
        });
        
        // Set globally
        window.sidebarTabManager = tabManager;
        
        // Also set it on builder prototype if possible - this ensures ALL builder instances have it
        try {
          if (builder.constructor && builder.constructor.prototype) {
            // Use getter/setter on prototype to always return tabManager
            Object.defineProperty(builder.constructor.prototype, 'settingsTabManager', {
              get: function() {
                return window.sidebarTabManager || tabManager;
              },
              set: function(value) {
                window.sidebarTabManager = value;
              },
              configurable: true,
              enumerable: true
            });
          }
        } catch (e) {
          console.warn('Could not set settingsTabManager on prototype:', e);
          // Fallback: just set it directly
          try {
            if (builder.constructor && builder.constructor.prototype) {
              builder.constructor.prototype.settingsTabManager = tabManager;
            }
          } catch (e2) {
            console.warn('Could not set settingsTabManager on prototype (fallback):', e2);
          }
        }
        
        // Make builder instance available globally (required by widgets)
        window.builder = builder;

        // Add error handler to catch BuilderJS errors
        if (window.builder && typeof window.builder.on === 'function') {
          window.builder.on('error', (error) => {
            // Suppress common BuilderJS errors that are non-critical
            if (error && error.message && (
              error.message.includes('frameElement') ||
              error.message.includes('setPosSize') ||
              error.message.includes('innerHTML') ||
              error.message.includes('openTab') ||
              error.message.includes('styleString') ||
              error.message.includes('RSSElement') ||
              error.message.includes('renderTemplate') ||
              error.message.includes('Cannot set properties of null') ||
              error.message.includes('Cannot read properties of null') ||
              error.message.includes('Cannot read properties of undefined') ||
              error.message.includes('renderElementControls') ||
              error.message.includes('selectElement') ||
              error.message.includes('unselect')
            )) {
              console.warn('BuilderJS error suppressed:', error.message);
              return;
            }
            console.error('BuilderJS error:', error);
            toast.error('BuilderJS error: ' + (error.message || 'Unknown error'));
          });
        }
        
        // Ensure settingsTabManager exists to prevent openTab errors
        if (builder.settingsTabManager) {
          window.sidebarTabManager = builder.settingsTabManager;
        } else {
          // Create a dummy settingsTabManager to prevent errors
          window.sidebarTabManager = {
            openTab: function() {
              console.warn('settingsTabManager.openTab called but not initialized');
              return false;
            }
          };
        }
        
        // Override Builder methods to add null checks - MUST be done before any element interactions
        // Use Object.defineProperty to ensure settingsTabManager is always accessible
        Object.defineProperty(builder, 'settingsTabManager', {
          get: function() {
            return window.sidebarTabManager || tabManager;
          },
          set: function(value) {
            window.sidebarTabManager = value;
          },
          configurable: true,
          enumerable: true
        });

        // Fix RSS widget styleString error by overriding renderTemplate
        if (builder.renderTemplate && typeof builder.renderTemplate === 'function') {
          const originalRenderTemplate = builder.renderTemplate.bind(builder);
          builder.renderTemplate = function(template, data) {
            try {
              // Ensure styleString is defined for RSS and other widgets
              if (!data) {
                data = {};
              }
              if (!data.styleString) {
                data.styleString = '';
              }
              
              return originalRenderTemplate.call(this, template, data);
            } catch (error) {
              if (error.message && error.message.includes('styleString')) {
                console.warn('RSS styleString error, using default:', error.message);
                // Retry with default styleString
                const safeData = { ...data, styleString: '' };
                try {
                  return originalRenderTemplate.call(this, template, safeData);
                } catch (e) {
                  console.warn('RSS renderTemplate error suppressed:', e.message);
                  return ''; // Return empty string on error
                }
              }
              throw error;
            }
          };
        }
        
        if (builder.unselect && typeof builder.unselect === 'function') {
          const originalUnselect = builder.unselect.bind(builder);
          builder.unselect = function(...args) {
            try {
              // Force set settingsTabManager before calling
              if (!this.settingsTabManager) {
                this.settingsTabManager = tabManager;
              }
              if (!window.sidebarTabManager) {
                window.sidebarTabManager = tabManager;
              }
              return originalUnselect.apply(this, args);
            } catch (error) {
              if (error && error.message && (
                error.message.includes('openTab') ||
                error.message.includes('Cannot read properties of undefined') ||
                error.message.includes('settingsTabManager')
              )) {
                console.warn('BuilderJS unselect error suppressed:', error.message);
                return;
              }
              throw error;
            }
          };
        }
        
        if (builder.selectElement && typeof builder.selectElement === 'function') {
          const originalSelectElement = builder.selectElement.bind(builder);
          builder.selectElement = function(...args) {
            try {
              // Force set settingsTabManager before calling
              if (!this.settingsTabManager) {
                this.settingsTabManager = tabManager;
              }
              if (!window.sidebarTabManager) {
                window.sidebarTabManager = tabManager;
              }
              return originalSelectElement.apply(this, args);
            } catch (error) {
              if (error && error.message && (
                error.message.includes('openTab') ||
                error.message.includes('innerHTML') ||
                error.message.includes('Cannot read properties of undefined') ||
                error.message.includes('Cannot set properties of null') ||
                error.message.includes('settingsTabManager')
              )) {
                console.warn('BuilderJS selectElement error suppressed:', error.message);
                return;
              }
              throw error;
            }
          };
        }
        
        // Override renderElementControls to add null checks - this is called when editing text
        if (builder.renderElementControls && typeof builder.renderElementControls === 'function') {
          const originalRenderElementControls = builder.renderElementControls.bind(builder);
          builder.renderElementControls = function(...args) {
            try {
              // Ensure settings container exists before rendering
              const settingsContainer = document.getElementById(settingsContainerRef.current.id);
              if (!settingsContainer) {
                console.warn('Settings container not found in renderElementControls, creating placeholder');
                const placeholder = document.createElement('div');
                placeholder.id = settingsContainerRef.current.id;
                placeholder.className = 'builderjs-settings';
                placeholder.style.cssText = 'width: 100%; height: 100%; overflow: auto;';
                if (settingsContainerRef.current && settingsContainerRef.current.parentElement) {
                  settingsContainerRef.current.parentElement.appendChild(placeholder);
                }
                return; // Don't render if container doesn't exist
              }
              
              // Ensure container has content area
              if (!settingsContainer.innerHTML.trim()) {
                settingsContainer.innerHTML = '<div class="builderjs-settings-content"></div>';
              }
              
              return originalRenderElementControls.apply(this, args);
            } catch (error) {
              if (error && error.message && (
                error.message.includes('innerHTML') ||
                error.message.includes('Cannot set properties of null') ||
                error.message.includes('Cannot read properties of null') ||
                error.message.includes('renderElementControls')
              )) {
                console.warn('BuilderJS renderElementControls error suppressed:', error.message);
                return;
              }
              throw error;
            }
          };
        }
        
        // Add event listeners to catch errors during text editing (input, keydown, keyup)
        const addTextEditErrorHandlers = () => {
          // Handle input events (typing, deleting)
          const inputHandler = (event) => {
            try {
              // Let the event proceed normally
            } catch (error) {
              if (error && error.message && (
                error.message.includes('innerHTML') ||
                error.message.includes('Cannot set properties of null') ||
                error.message.includes('Cannot read properties of null') ||
                error.message.includes('Cannot read properties of undefined')
              )) {
                console.warn('Text edit input error suppressed:', error.message);
                event.preventDefault();
                event.stopPropagation();
                return false;
              }
            }
          };
          
          // Handle keydown events (backspace, delete, etc.)
          const keydownHandler = (event) => {
            try {
              // Let the event proceed normally
            } catch (error) {
              if (error && error.message && (
                error.message.includes('innerHTML') ||
                error.message.includes('Cannot set properties of null') ||
                error.message.includes('Cannot read properties of null')
              )) {
                console.warn('Text edit keydown error suppressed:', error.message);
                event.preventDefault();
                event.stopPropagation();
                return false;
              }
            }
          };
          
          // Add listeners to main container where text editing happens
          if (mainContainerRef.current) {
            mainContainerRef.current.addEventListener('input', inputHandler, { capture: true, passive: true });
            mainContainerRef.current.addEventListener('keydown', keydownHandler, { capture: true, passive: true });
            mainContainerRef.current.addEventListener('keyup', keydownHandler, { capture: true, passive: true });
          }
          
          // Also add to document for inline editing
          document.addEventListener('input', inputHandler, { capture: true, passive: true });
          document.addEventListener('keydown', keydownHandler, { capture: true, passive: true });
          
          // Store handlers for cleanup
          if (!window._builderTextEditHandlers) {
            window._builderTextEditHandlers = {
              input: inputHandler,
              keydown: keydownHandler,
              container: mainContainerRef.current
            };
          }
        };
        
        // Add text edit error handlers after a short delay
        setTimeout(addTextEditErrorHandlers, 300);

        // Initialize with theme or load existing data
        if (initialData) {
          if (initialData.url) {
            // If it's a URL, use loadFromUrl
            builder.loadFromUrl(initialData.url, () => {
              // Ensure widgets container is visible
              if (widgetsContainerRef.current) {
                widgetsContainerRef.current.style.display = 'flex';
                widgetsContainerRef.current.style.visibility = 'visible';
                widgetsContainerRef.current.style.opacity = '1';
              }
              
              setIsInitialized(true);
              setIsLoading(false);
              toast.success('Page loaded successfully');
            });
          } else {
            // Parse JSON data
            let jsonData;
            if (typeof initialData === 'string') {
              try {
                jsonData = JSON.parse(initialData);
              } catch (e) {
                jsonData = { elementLists: [], formats: {} };
              }
            } else {
              jsonData = initialData;
            }

            // Ensure proper structure
            if (!jsonData.elementLists) {
              jsonData.elementLists = [];
            }
            if (!jsonData.formats) {
              jsonData.formats = {};
            }

            // Load with callback
            builder.load(jsonData, themeUrl, () => {
              // Ensure widgets container is visible
              if (widgetsContainerRef.current) {
                widgetsContainerRef.current.style.display = 'flex';
                widgetsContainerRef.current.style.visibility = 'visible';
                widgetsContainerRef.current.style.opacity = '1';
              }
              
              setIsInitialized(true);
              setIsLoading(false);
              toast.success('Page loaded successfully');
            });
          }
        } else {
          // Initialize with blank page
          const emptyData = {
            theme: 'Blank',
            elementLists: [],
            formats: {}
          };
          
          builder.load(emptyData, themeUrl, () => {
            // Set up sidebarTabManager if it exists
            if (builder.settingsTabManager && !window.sidebarTabManager) {
              window.sidebarTabManager = builder.settingsTabManager;
            }
            
            // Ensure widgets container is visible and check for widgets
            setTimeout(() => {
              if (widgetsContainerRef.current) {
                widgetsContainerRef.current.style.display = 'flex';
                widgetsContainerRef.current.style.visibility = 'visible';
                widgetsContainerRef.current.style.opacity = '1';
                
                // Debug: Check widgets
                const widgetCount = widgetsContainerRef.current.children.length;
                
                if (widgetCount === 0) {
                  console.warn('No widgets found in container. BuilderJS may need more time to load widgets.');
                  // Check if builder has widgetsBox
                  if (builder.widgetsBox) {
                    // BuilderJS widgetsBox exists
                  } else {
                    console.warn('BuilderJS widgetsBox not found');
                  }
                }
              }
            }, 500);
            
            setIsInitialized(true);
            setIsLoading(false);
          });
        }

      } catch (error) {
        console.error('Error initializing BuilderJS:', error);
        
        // Suppress frameElement errors as they're often non-critical
        if (error.message && error.message.includes('frameElement')) {
          console.warn('BuilderJS frameElement error (non-critical):', error.message);
          // Continue initialization despite this error
          setIsInitialized(true);
          setIsLoading(false);
        } else {
          toast.error('Failed to initialize BuilderJS: ' + error.message);
          setIsLoading(false);
        }
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      initializeBuilder();
    }, 100);

    return () => {
      mounted = false;
      clearTimeout(timer);
      window.removeEventListener('error', errorHandler, true);
      window.removeEventListener('unhandledrejection', (event) => {
        errorHandler({ error: event.reason, preventDefault: () => {}, stopPropagation: () => {}, stopImmediatePropagation: () => {} });
      });
      
      // Remove scroll error handlers
      if (mainContainerRef.current) {
        mainContainerRef.current.removeEventListener('scroll', scrollErrorHandler, { capture: true });
      }
      if (widgetsContainerRef.current) {
        widgetsContainerRef.current.removeEventListener('scroll', scrollErrorHandler, { capture: true });
      }
      window.removeEventListener('scroll', scrollErrorHandler, { capture: true });
      
      // Remove text edit error handlers
      if (window._builderTextEditHandlers) {
        const handlers = window._builderTextEditHandlers;
        if (handlers.container) {
          handlers.container.removeEventListener('input', handlers.input, { capture: true });
          handlers.container.removeEventListener('keydown', handlers.keydown, { capture: true });
          handlers.container.removeEventListener('keyup', handlers.keydown, { capture: true });
        }
        document.removeEventListener('input', handlers.input, { capture: true });
        document.removeEventListener('keydown', handlers.keydown, { capture: true });
        delete window._builderTextEditHandlers;
      }
      
      // Clean up MutationObserver for inline controls
      if (window._builderInlineControlsObserver) {
        window._builderInlineControlsObserver.disconnect();
        delete window._builderInlineControlsObserver;
      }
      
      // Clean up MutationObserver for inline controls
      if (window._builderInlineControlsObserver) {
        window._builderInlineControlsObserver.disconnect();
        delete window._builderInlineControlsObserver;
      }
      
      // Clean up interval for inline controls
      if (window._builderInlineControlsInterval) {
        clearInterval(window._builderInlineControlsInterval);
        delete window._builderInlineControlsInterval;
      }
      
      // Clean up click handler for inline controls
      if (window._builderInlineControlsClickHandler && mainContainerRef.current) {
        mainContainerRef.current.removeEventListener('click', window._builderInlineControlsClickHandler, true);
        delete window._builderInlineControlsClickHandler;
      }
      
      // Clean up global builder reference
      if (window.builder === builderInstanceRef.current) {
        delete window.builder;
      }
      if (window.sidebarTabManager) {
        delete window.sidebarTabManager;
      }
    };
  }, [initialData, themeUrl]);

  const handleSave = () => {
    if (!builderInstanceRef.current) {
      toast.error('BuilderJS is not initialized');
      return;
    }

    try {
      // Check available methods
      const builder = builderInstanceRef.current;
      
      // Try getData() first (most common BuilderJS method)
      let jsonData;
      if (typeof builder.getData === 'function') {
        jsonData = builder.getData();
      } else if (typeof builder.getJson === 'function') {
        jsonData = builder.getJson();
      } else {
        throw new Error('BuilderJS instance does not have getData() or getJson() method');
      }
      
      // Get HTML data
      let htmlData = '';
      if (typeof builder.getHtml === 'function') {
        htmlData = builder.getHtml();
      } else {
        console.warn('BuilderJS instance does not have getHtml() method');
      }
      
      // Ensure jsonData is an object, not a string
      let jsonObject = jsonData;
      if (typeof jsonData === 'string') {
        try {
          jsonObject = JSON.parse(jsonData);
        } catch (e) {
          console.warn('JSON data is not valid JSON string, using as object');
          jsonObject = jsonData;
        }
      }
      
      if (onSave) {
        onSave(jsonObject, htmlData);
      } else {
        // Default save behavior
        toast.success('Page data saved');
      }
    } catch (error) {
      console.error('Error saving page:', error);
      toast.error('Failed to save page: ' + error.message);
    }
  };

  const handleExport = () => {
    if (!builderInstanceRef.current) {
      toast.error('BuilderJS is not initialized');
      return;
    }

    try {
      const htmlData = builderInstanceRef.current.getHtml();
      
      if (onExport) {
        onExport(htmlData);
      } else {
        // Default export behavior - create download
        const blob = new Blob([htmlData], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'page.html';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Page exported successfully');
      }
    } catch (error) {
      console.error('Error exporting page:', error);
      toast.error('Failed to export page: ' + error.message);
    }
  };

  const handleClear = () => {
    if (!builderInstanceRef.current) {
      return;
    }

    if (window.confirm('Are you sure you want to clear the page? This action cannot be undone.')) {
      try {
        const emptyData = {
          theme: 'Blank',
          elementLists: [],
          formats: {}
        };
        builderInstanceRef.current.load(emptyData, themeUrl, () => {
          toast.success('Page cleared');
        });
      } catch (error) {
        console.error('Error clearing page:', error);
        toast.error('Failed to clear page');
      }
    }
  };

  const handleReset = () => {
    if (!builderInstanceRef.current) {
      return;
    }

    if (window.confirm('Are you sure you want to reset the page? All unsaved changes will be lost.')) {
      try {
        if (initialData) {
          if (initialData.url) {
            builderInstanceRef.current.loadFromUrl(initialData.url, () => {
              toast.success('Page reset');
            });
          } else {
            let jsonData = typeof initialData === 'string' 
              ? JSON.parse(initialData) 
              : initialData;
            
            if (!jsonData.elementLists) jsonData.elementLists = [];
            if (!jsonData.formats) jsonData.formats = {};
            
            builderInstanceRef.current.load(jsonData, themeUrl, () => {
              toast.success('Page reset');
            });
          }
        } else {
          const emptyData = {
            theme: 'Blank',
            elementLists: [],
            formats: {}
          };
          builderInstanceRef.current.load(emptyData, themeUrl, () => {
            toast.success('Page reset');
          });
        }
      } catch (error) {
        console.error('Error resetting page:', error);
        toast.error('Failed to reset page');
      }
    }
  };

  return (
    <div className="new-builderjs-container">
      {/* Toolbar */}
      <div className="builderjs-toolbar">
        <div className="toolbar-left">
          <div className="toolbar-layout-info">
            <span className="layout-icon">📐</span>
            <span className="layout-text">1 Column Layout</span>
            <button className="toolbar-icon-btn" title="Edit Layout">
              <span>✏️</span>
            </button>
          </div>
          <button 
            className="toolbar-btn toolbar-btn-theme" 
            title="Change Theme"
          >
            Change Theme
          </button>
        </div>
        
        <div className="toolbar-center">
          <div className="device-preview">
            <button className="device-btn active" title="Desktop View">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-2v2a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-2H2a2 2 0 0 1-2-2V2zm13 0H3v8h10V2z"/>
              </svg>
            </button>
            <button className="device-btn" title="Tablet View">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M1 4a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V4zm1 0v8h12V4H2z"/>
              </svg>
            </button>
            <button className="device-btn" title="Mobile View">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M11 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h6zM5 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H5z"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="toolbar-right">
          <button 
            className="toolbar-btn toolbar-btn-danger" 
            onClick={handleClear}
            title="Clear Page"
          >
            Clear
          </button>
          <button 
            className="toolbar-btn" 
            onClick={handleReset}
            title="Reset Page"
          >
            Reset
          </button>
          <button 
            className="toolbar-btn toolbar-btn-primary" 
            onClick={handleSave}
            title="Save Page"
          >
            Save
          </button>
          <button 
            className="toolbar-btn toolbar-btn-success" 
            onClick={handleExport}
            title="Download Bundle"
          >
            Download bundle
          </button>
          <div className="toolbar-dropdown">
            <button className="toolbar-btn toolbar-btn-export" title="Export">
              Export
              <span className="dropdown-arrow">▼</span>
            </button>
          </div>
          <button className="toolbar-icon-btn" title="Settings">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
              <path d="M18 9.5a1.5 1.5 0 0 1-1.5 1.5h-1.76a4.49 4.49 0 0 1-.73 1.75l1.25 1.27a1.5 1.5 0 0 1 0 2.12l-1.06 1.06a1.5 1.5 0 0 1-2.12 0l-1.27-1.25a4.49 4.49 0 0 1-1.75.73V16.5a1.5 1.5 0 0 1-1.5 1.5h-1.5a1.5 1.5 0 0 1-1.5-1.5v-1.76a4.49 4.49 0 0 1-1.75-.73l-1.27 1.25a1.5 1.5 0 0 1-2.12 0L2.24 15.74a1.5 1.5 0 0 1 0-2.12l1.25-1.27a4.49 4.49 0 0 1-.73-1.75H1.5A1.5 1.5 0 0 1 0 9.5V8a1.5 1.5 0 0 1 1.5-1.5h1.76a4.49 4.49 0 0 1 .73-1.75L2.24 3.48a1.5 1.5 0 0 1 0-2.12L3.3.3a1.5 1.5 0 0 1 2.12 0l1.27 1.25a4.49 4.49 0 0 1 1.75-.73V1.5A1.5 1.5 0 0 1 10.5 0H12a1.5 1.5 0 0 1 1.5 1.5v1.76a4.49 4.49 0 0 1 1.75.73l1.27-1.25a1.5 1.5 0 0 1 2.12 0l1.06 1.06a1.5 1.5 0 0 1 0 2.12l-1.25 1.27a4.49 4.49 0 0 1 .73 1.75H16.5A1.5 1.5 0 0 1 18 8v1.5zM9 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6z"/>
            </svg>
          </button>
          <button className="toolbar-icon-btn" title="History">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
              <path d="M9 0C4.03 0 0 4.03 0 9s4.03 9 9 9c2.3 0 4.4-.88 5.96-2.32l-1.06-1.06C12.64 16.62 10.92 17.4 9 17.4c-4.64 0-8.4-3.76-8.4-8.4S4.36.6 9 .6c4.64 0 8.4 3.76 8.4 8.4 0 1.1-.21 2.15-.59 3.12l1.47 1.47C18.88 12.4 19.8 10.3 19.8 9c0-5.97-4.83-10.8-10.8-10.8z"/>
              <path d="M9 4.2v4.8l3.6 2.16.72-1.2L10.2 8.4V4.2H9z"/>
            </svg>
          </button>
          <button className="toolbar-icon-btn active" title="Menu">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
              <path d="M0 3h18v2H0V3zm0 5h18v2H0V8zm0 5h18v2H0v-2z"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="builderjs-content-wrapper">
        {/* Main Builder Container */}
        <div className="builderjs-main-container">
          <div 
            id="builderjs-main" 
            ref={mainContainerRef}
            className="builderjs-main"
          />
        </div>

        {/* Widgets Container - Right Side */}
        <div 
          id="builderjs-widgets-container"
          ref={widgetsContainerRef}
          className="builderjs-widgets-container"
        />

        {/* Settings Panel - Far Right Side */}
        <div className="builderjs-settings-panel">
          <div 
            id="builderjs-settings" 
            ref={settingsContainerRef}
            className="builderjs-settings"
          />
        </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="builderjs-loading">
          <div className="loading-spinner"></div>
          <p>Loading BuilderJS...</p>
        </div>
      )}
    </div>
  );
};

export default NewBuilderJS;


